/**
 * Dashboard week macros — 7 days ending today.
 * Past days: one GET per app session (shared menu cache, staleTime ∞) unless subscription
 * particulars already supply macros. Uses active menu stack keys (`menu` vs `food-menu`).
 */
import { useMemo, useCallback, useState, useEffect } from 'react';
import moment from 'moment';
import { useQueries, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '../../../shared/auth/store';
import { useSubscriptionContext } from '../subscription/hooks';
import { MENU_CACHE_GC_MS, MENU_CACHE_STALE_MS } from '../menu/hooks';
import {
  buildMacroWeekDateList,
  isPastMenuDate,
  MACRO_WEEK_DAY_COUNT,
  PAST_DATE_MENU_STALE_MS,
} from '../menu/cache';
import { ensureAsciiDateString } from '../../../shared/utils/apiParams';
import type { DayMacroSnapshot } from './types';
import {
  buildDayMacroMapForDashboardWeek,
  computeMacrosFromBuilders,
  type MacroTotals,
} from '../menu/macroUtils';
import {
  activeFetchMenu,
  activeMenuDayKey,
  useActiveMenuStack,
} from '../activeMenu';

const DATE_LIST = buildMacroWeekDateList();

function hasMacroTotals(m: MacroTotals | DayMacroSnapshot | null | undefined): boolean {
  if (!m) {
    return false;
  }
  return (m.calories ?? 0) > 0 || (m.protein ?? 0) > 0 || (m.carbs ?? 0) > 0 || (m.fats ?? 0) > 0;
}

function mergeDayMacros(
  date: string,
  menu_builders: Parameters<typeof computeMacrosFromBuilders>[0],
  subscription_macro_map: Map<string, MacroTotals>,
): DayMacroSnapshot {
  const from_menu = computeMacrosFromBuilders(menu_builders);
  if (hasMacroTotals(from_menu)) {
    return { date, ...from_menu! };
  }
  const from_sub = subscription_macro_map.get(date);
  if (hasMacroTotals(from_sub)) {
    return { date, ...from_sub! };
  }
  return { date, calories: 0, protein: 0, carbs: 0, fats: 0 };
}

function isMacroDayResolved(
  query_client: ReturnType<typeof useQueryClient>,
  customer_id: number,
  date: string,
  is_fetched: boolean,
  subscription_macros: MacroTotals | undefined,
): boolean {
  if (hasMacroTotals(subscription_macros) && isPastMenuDate(date)) {
    return true;
  }
  if (is_fetched) {
    return true;
  }
  if (!isPastMenuDate(date)) {
    return false;
  }
  const date_ascii = ensureAsciiDateString(date) || date;
  return query_client.getQueryData(activeMenuDayKey(customer_id, date_ascii, false)) != null;
}

export type UseProgressiveMacroWeekDataOptions = {
  /**
   * Only one dashboard subscriber should drive network (`true`).
   * Other observers pass `false` and read the shared React Query cache.
   */
  enable_fetch?: boolean;
};

export function useProgressiveMacroWeekData(
  options?: UseProgressiveMacroWeekDataOptions,
): {
  data: DayMacroSnapshot[];
  dateFrom: string;
  dateTo: string;
  loaded: boolean;
  refetch: () => void;
} {
  const enable_fetch = options?.enable_fetch === true;
  const customer_id = useAuthStore(s => s.user?.id);
  const query_client = useQueryClient();
  const menu_stack = useActiveMenuStack();
  const { activeSubscriptions, isLoading: subscription_loading, refetch: refetch_subscription } = useSubscriptionContext();
  const [max_enabled_idx, set_max_enabled_idx] = useState(0);

  const subscription_macro_map = useMemo(
    () => buildDayMacroMapForDashboardWeek(activeSubscriptions, DATE_LIST),
    [activeSubscriptions],
  );

  const menu_queries = useQueries({
    queries: DATE_LIST.map((date, idx) => {
      const date_ascii = ensureAsciiDateString(date) || date;
      const is_past = isPastMenuDate(date_ascii);
      const key =
        typeof customer_id === 'number'
          ? activeMenuDayKey(customer_id, date_ascii, false)
          : (['menu', 0, date_ascii, false, menu_stack] as const);
      const has_cached =
        typeof customer_id === 'number' && query_client.getQueryData(activeMenuDayKey(customer_id, date_ascii, false)) != null;
      const sub_macros = subscription_macro_map.get(date_ascii);
      const skip_for_sub_macros = is_past && hasMacroTotals(sub_macros);

      return {
        queryKey: key,
        queryFn: () => activeFetchMenu(customer_id!, date_ascii, false),
        enabled:
          enable_fetch &&
          typeof customer_id === 'number' &&
          idx <= max_enabled_idx &&
          !skip_for_sub_macros &&
          (!is_past || !has_cached),
        staleTime: is_past ? PAST_DATE_MENU_STALE_MS : MENU_CACHE_STALE_MS,
        gcTime: is_past ? PAST_DATE_MENU_STALE_MS : MENU_CACHE_GC_MS,
        refetchOnMount: is_past ? false : true,
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
      };
    }),
  });

  const current_menu_query = menu_queries[max_enabled_idx];
  const current_date = DATE_LIST[max_enabled_idx];

  useEffect(() => {
    if (!enable_fetch || typeof customer_id !== 'number' || !current_date) {
      return;
    }
    const date_ascii = ensureAsciiDateString(current_date) || current_date;
    const resolved = isMacroDayResolved(
      query_client,
      customer_id,
      current_date,
      current_menu_query?.isFetched ?? false,
      subscription_macro_map.get(date_ascii),
    );
    if (resolved && max_enabled_idx < DATE_LIST.length - 1) {
      set_max_enabled_idx(prev => prev + 1);
    }
  }, [
    enable_fetch,
    customer_id,
    max_enabled_idx,
    current_date,
    current_menu_query?.isFetched,
    query_client,
    subscription_macro_map,
    menu_stack,
  ]);

  const data = useMemo((): DayMacroSnapshot[] => {
    return DATE_LIST.map((date, idx) => {
      const date_ascii = ensureAsciiDateString(date) || date;
      const from_query = menu_queries[idx]?.data?.menu_builders;
      const from_cache =
        typeof customer_id === 'number'
          ? (query_client.getQueryData(activeMenuDayKey(customer_id, date_ascii, false)) as
              | { menu_builders?: Parameters<typeof computeMacrosFromBuilders>[0] }
              | undefined)
          : undefined;
      const builders = from_query ?? from_cache?.menu_builders;
      return mergeDayMacros(date, builders, subscription_macro_map);
    });
  }, [menu_queries, subscription_macro_map, customer_id, query_client, menu_stack]);

  const date_from = DATE_LIST[0] ?? moment().subtract(MACRO_WEEK_DAY_COUNT - 1, 'days').format('YYYY-MM-DD');
  const date_to = DATE_LIST[DATE_LIST.length - 1] ?? moment().format('YYYY-MM-DD');

  const any_menu_resolved = menu_queries.some((q, idx) => {
    const date = DATE_LIST[idx]!;
    const date_ascii = ensureAsciiDateString(date) || date;
    return (
      idx <= max_enabled_idx &&
      isMacroDayResolved(
        query_client,
        customer_id ?? 0,
        date,
        q.isFetched,
        subscription_macro_map.get(date_ascii),
      )
    );
  });
  const loaded =
    !subscription_loading ||
    any_menu_resolved ||
    data.some(hasMacroTotals) ||
    [...subscription_macro_map.values()].some(hasMacroTotals);

  /** Refresh subscription fallback macros only — past menu GETs stay cached for the session. */
  const refetch = useCallback(() => {
    void refetch_subscription();
  }, [refetch_subscription]);

  return useMemo(
    () => ({
      data,
      dateFrom: date_from,
      dateTo: date_to,
      loaded,
      refetch,
    }),
    [data, date_from, date_to, loaded, refetch],
  );
}
